import { Lock } from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import { ServerStatusNotice, SUPPORT_TG } from '@/components/common/ServerStatusNotice';

/**
 * Full-height "we could not load this" state. Since Алексей 26.10 #2635/#2639 it is
 * {@link ServerStatusNotice}: says whether the user is offline, the server is
 * updating (progress + robot, reloads data by itself) or it is genuinely unclear —
 * calm colours, the robot instead of the red warning icon.
 */
export function DataErrorState({ className, error, onRetry }: { className?: string; error?: unknown; onRetry?: () => void }) {
  return <ServerStatusNotice variant="page" error={error} onRetry={onRetry} className={className} />;
}

/**
 * Shown when a URL points at a branch the signed-in user cannot see — following
 * a shared link to someone else's spot used to land on a generic load failure,
 * which reads as "the product is broken" rather than "this is not yours"
 * (Alexey 09.09).
 */
export function NoBranchAccessState({ className }: { className?: string }) {
  const { t } = useT();
  return (
    <div
      className={cn(
        'mx-auto flex min-h-[calc(100vh-8rem)] w-full max-w-2xl flex-col items-center justify-center gap-3 px-4 py-12 text-center',
        className,
      )}
    >
      <span className="flex size-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
        <Lock className="size-6" />
      </span>
      <h2 className="text-balance text-xl font-semibold tracking-tight text-foreground">
        {t('noBranchAccess.title')}
      </h2>
      <p className="max-w-xl text-balance text-sm text-muted-foreground">
        {t('noBranchAccess.subtitle')}{' '}
        <a
          href={`https://t.me/${SUPPORT_TG}`}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          @{SUPPORT_TG}
        </a>
        .
      </p>
    </div>
  );
}

export default DataErrorState;
